import { Link } from 'react-router-dom';
import Instrument from './Instrument';
import { ACCENT_HEX, type Channel } from '@/data/channels';

/**
 * 一个方向 = 机架上的一格。
 *
 * 三个通道用完全相同的版式，只换通道色和仪器——机架上的设备是对齐的，
 * 左右交替反而会破坏那种「一整排仪表」的秩序感。
 * 能力条目做成数据表（hairline 分隔的行）而不是卡片网格：datasheet 是这个
 * 主题的原生版式，卡片网格是任何站都能套的默认答案。
 */
export default function ChannelSection({ channel }: { channel: Channel }) {
  const hex = ACCENT_HEX[channel.accent];

  return (
    <section
      id={channel.id}
      className="border-t border-rack-bone/[0.08] bg-rack-ground py-20 sm:py-28"
      aria-labelledby={`${channel.id}-title`}
    >
      <div className="mx-auto max-w-[1180px] px-6 sm:px-10">
        {/* 通道抬头 */}
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <span className="font-readout text-[11px] tracking-[0.22em]" style={{ color: hex }}>
            {channel.no}
          </span>
          <h2
            id={`${channel.id}-title`}
            className="font-songti text-[clamp(1.5rem,4vw,2.15rem)] font-semibold tracking-[0.14em] text-rack-bone"
          >
            {channel.name}
          </h2>
          <span className="font-readout text-[10px] uppercase tracking-[0.3em] text-rack-bone/22">
            {channel.en}
          </span>
        </div>

        <p
          className="mt-7 max-w-[30ch] font-songti text-[clamp(1.15rem,2.9vw,1.6rem)] font-semibold leading-[1.5] tracking-[0.08em] text-rack-bone/92"
        >
          {channel.thesis}
        </p>
        <p className="mt-5 max-w-[62ch] text-[13.5px] leading-[1.95] text-rack-bone/50">
          {channel.lede}
        </p>

        {/* 铭牌 + 仪器 */}
        <div className="mt-11 grid gap-6 lg:grid-cols-[190px_1fr]">
          <dl className="flex flex-col justify-start gap-0 self-start border-t border-rack-bone/10">
            {channel.specs.map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-rack-bone/[0.07] py-2.5"
              >
                <dt className="font-readout text-[9.5px] uppercase tracking-[0.16em] text-rack-bone/28">
                  {k}
                </dt>
                <dd className="text-right font-readout text-[11px] text-rack-bone/72">{v}</dd>
              </div>
            ))}
          </dl>
          <Instrument kind={channel.instrument} label={channel.instrumentLabel} />
        </div>

        {/* 能力数据表 */}
        <div className="mt-14 border-t border-rack-bone/12">
          {channel.capabilities.map((cap, i) => (
            <article
          key={cap.title}
              className="grid gap-x-7 gap-y-3 border-b border-rack-bone/[0.07] py-7 md:grid-cols-[44px_1fr_168px]"
            >
              <div className="flex items-start gap-2.5">
                <span className="mt-[7px] h-px w-2.5 shrink-0" style={{ background: hex }} />
                <span className="font-readout text-[11px] text-rack-bone/30">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <div>
                <h3 className="text-[15px] font-medium leading-snug text-rack-bone/92">{cap.title}</h3>
                <p className="mt-2.5 max-w-[58ch] text-[13px] leading-[1.95] text-rack-bone/48">
                  {cap.detail}
                </p>
              </div>
              <ul className="flex flex-wrap gap-x-3 gap-y-1.5 md:flex-col md:gap-y-2">
                {cap.tags.map((tg) => (
                  <li
          key={tg}
          className="font-readout text-[10px] uppercase tracking-[0.1em] text-rack-bone/26"
                  >
                    {tg}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {channel.note && (
          <p className="mt-6 max-w-[62ch] text-[11.5px] leading-relaxed text-rack-bone/28">
            {channel.note}
          </p>
        )}

        {/* 案例入口。首页只讲做法，「能给客户什么」放到抽屉里——想深入的客户才点进去，
            首页不因此变长。 */}
        <Link
          to={`/cases/${channel.id}`}
          className="group mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-rack-bone/12 pt-6 transition-colors"
        >
          <span className="font-readout text-[9.5px] uppercase tracking-[0.18em] text-rack-bone/26">
            CASES
          </span>
          <span
            className="text-[14px] tracking-[0.02em] text-rack-bone/72 transition-colors group-hover:text-rack-bone"
            style={{ borderColor: hex }}
          >
            {channel.name}能交付什么 · 业务领域与做过的项目
          </span>
          <span
            className="font-readout text-[13px] transition-transform group-hover:translate-x-1"
            style={{ color: hex }}
          >
            →
          </span>
        </Link>
      </div>
    </section>
  );
}
